import {DataTableColumn} from 'mantine-datatable';
import {I18n} from 'aws-amplify/utils';
import {NavLink} from 'react-router-dom';
import {
    ActionIcon,
    Group,
} from '@mantine/core';
import {AiOutlineLink} from 'react-icons/ai';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {DomainName} from '../types';

// FNP-1282 §3a — THE COLUMNS, as `DataTableColumn<DomainName>[]`.
//
// This replaces `tables/columns.tsx`, which the checklist calls out by name:
// columns belong in `hooks/use<Module>TableColumns.tsx`. The move is not
// cosmetic — the old array was imported by `useDomainNames`, which meant the
// data hook owned the presentation config and the table read its columns back
// out of the hook that fetches records. Sorting is a table concern, so the
// table now asks for its own columns.
//
// ⚠️ EVERY COLUMN IS `sortable: true`. §3a allows an unsortable column only
// with a stated reason, and none of these five has one: all five carry a real
// `accessor`, so all five sort.
//
// ⚠️ SORTING IS A THREE-PART CONTRACT and this file is only part one. Parts two
// and three (`sortStatus`/`onSortStatusChange` on the table, and the comparator
// applied to the records) live in `tables/RecordsTable.tsx` and
// `hooks/useDomainNames.ts`. Setting `sortable` alone renders headers that look
// clickable and do nothing — strictly worse than no sorting, and the exact
// state flexi-email-app is in.
//
// ⚠️ NO COLUMN HERE DECLARES A WIDTH, and it must stay that way.
//
// FNP-1284: the fixed pixel widths were the cause of the phantom horizontal
// scrollbar, not the `2fr` four rounds of analysis argued about. Pinning
// columns commits their pixels before the flexible column gets anything, so
// the flexible one content-sizes to its longest value and pushes the table
// past its container. Removing every width fixed it, confirmed in a browser.
//
// `minWidth` in px is valid on a cell if a floor is ever wanted. A grid
// fraction is not. See `src/features/zones/tests/README-column-widths.md`.

export const useDomainNamesTableColumns = () => {

    const columns: DataTableColumn<DomainName>[] = [
        {
            accessor: 'name',
            sortable: true,
            title: I18n.get('Name'),
            // The row's front door — no width, so it claims the slack.
            render: (item: DomainName) => (
                <Group>
                    <NavLink to={`/domain-names/${item.id}`}>{item.name}</NavLink>
                    {/* FNP-1282: icon-only, so the label IS the accessible
                        name. Without it a screen reader announces only
                        "button". Named per row so the announcement says WHICH
                        site it opens. */}
                    <ActionIcon
                        variant={'subtle'}
                        aria-label={`Open ${item.name ?? 'site'} in a new tab`}
                        title={`Open ${item.name ?? 'site'} in a new tab`}
                        onClick={() => window.open('http://' + item.name)}
                    >
                        <AiOutlineLink/>
                    </ActionIcon>
                </Group>
            ),
        },
        {
            accessor: 'registrarId',
            sortable: true,
            title: I18n.get('Registrar'),
            // ⚠️ SORTS BY `registrarId`, NOT by the registrar's name. The name
            // arrives on a resolved sub-object (`item.registrar?.name`) that
            // `sortRecords` cannot reach through a flat accessor, and inventing
            // a synthetic column to hold it would put an id-shaped string in
            // front of the operator. Grouping by registrar is what the sort
            // gives you here, which is the useful behaviour anyway.
            render: (item: DomainName) => (
                <NavLink to={`/registrars/${item.registrarId}`}>{item.registrar?.name}</NavLink>
            ),
        },
        {
            accessor: 'category',
            sortable: true,
            title: I18n.get('Category'),
            render: (item: DomainName) => item.category,
        },
        {
            accessor: 'tldId',
            sortable: true,
            title: I18n.get('TLD'),
            // Same as `registrarId` above: the display name is on a resolved
            // sub-object, the accessor is the id the sort orders by.
            render: (item: DomainName) => (
                <NavLink to={`/top-level-domains/${item.tldId}`}>{item.topLevelDomain?.name}</NavLink>
            ),
        },
        {
            accessor: 'created',
            sortable: true,
            title: I18n.get('Created'),
            render: (item: DomainName) => formatTimestamp(item.created),
        },
    ];

    return {
        columns,
    };
};
